import React, { useState, useRef, useEffect } from 'react';
import Sidebar from './ui/Sidebar';
import { Menu, Bell, Check, Trash2, X, LogOut, RotateCw } from 'lucide-react';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { HeaderProvider } from '../context/HeaderContext';

const Layout = ({ children }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDesktopOpen, setIsDesktopOpen] = useState(true);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [headerContent, setHeaderContent] = useState(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotifications } = useNotification();
  const { currentUser, logout } = useAuth();
  const notifRef = useRef(null);
  const isTouchDevice = navigator.maxTouchPoints > 0 || window.matchMedia('(any-pointer: coarse)').matches;

  // Check if mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [viewportOrientation, setViewportOrientation] = useState(
    window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait'
  );
  const [forcedOrientation, setForcedOrientation] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      setViewportOrientation(window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait');
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const releaseInstalledOrientation = async () => {
      if (!window.matchMedia('(display-mode: standalone)').matches || !window.screen?.orientation) return;
      try {
        window.screen.orientation.unlock();
        await window.screen.orientation.lock('any');
      } catch (error) {
        // Some Android browsers require a user gesture; the header control is
        // the fallback for those installations.
      }
    };
    releaseInstalledOrientation();
  }, []);

  const rotateScreen = async () => {
    const current = forcedOrientation || viewportOrientation;
    const target = current === 'portrait' ? 'landscape' : 'portrait';
    try {
      if (!window.screen?.orientation?.lock) throw new Error('Orientation API unavailable');
      await window.screen.orientation.lock(target);
      setForcedOrientation(null);
    } catch (error) {
      // Tablets and embedded Android browsers often reject orientation.lock().
      // Rotate the CRM itself so the control still works without OS support.
      setForcedOrientation(target);
    }
  };

  const needsVisualRotation = Boolean(forcedOrientation && forcedOrientation !== viewportOrientation);
  const rotatedViewportStyle = needsVisualRotation
    ? {
        position: 'fixed',
        left: '50%',
        top: '50%',
        width: '100dvh',
        height: '100dvw',
        transform: 'translate(-50%, -50%) rotate(90deg)',
        transformOrigin: 'center',
      }
    : undefined;

  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setIsNotifOpen(false);
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getBgColor = (type) => {
    switch(type) {
      case 'warning': return 'bg-amber-50 border-amber-200 text-amber-700';
      case 'error': return 'bg-red-50 border-red-200 text-red-700';
      case 'success': return 'bg-emerald-50 border-emerald-200 text-emerald-700';
      default: return 'bg-slate-50 border-slate-200 text-slate-700';
    }
  };

  return (
    <HeaderProvider value={{ headerContent, setHeaderContent }}>
    <div className="flex h-[100dvh] min-h-0 overflow-hidden bg-slate-50" style={rotatedViewportStyle}>
      {/* Mobile Backdrop */}
      {isMobileOpen && (
        <div
          data-testid="mobile-backdrop"
          className="fixed inset-0 bg-black/60 z-30 lg:hidden backdrop-blur-sm animate-fade-in"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <Sidebar
        isOpen={isMobile ? isMobileOpen : isDesktopOpen}
        onToggle={() => isMobile ? setIsMobileOpen(!isMobileOpen) : setIsDesktopOpen(!isDesktopOpen)}
        isMobile={isMobile}
        onCloseMobile={() => setIsMobileOpen(false)}
      />

      {/* Main Content */}
      <div className={`
        flex h-full flex-1 min-h-0 flex-col min-w-0 overflow-hidden transition-all duration-300 ease-in-out
        ${!isMobile ? (isDesktopOpen ? 'ml-52' : 'ml-16') : 'ml-0'}
      `}>
        {/* Header */}
        <header 
          data-testid="main-header"
          className="sticky top-0 z-20 flex flex-shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 py-2 shadow-sm sm:px-6"
        >
          <div className="flex shrink-0 items-center gap-4">
            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              onClick={() => isMobile ? setIsMobileOpen(true) : setIsDesktopOpen(!isDesktopOpen)}
              className="p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
            >
              <Menu size={22} />
            </button>

            {/* Logo for mobile */}
            <span className="font-heading font-bold text-slate-800 text-lg lg:hidden">NexoHub</span>
          </div>

          <div className="mx-4 hidden min-w-0 flex-1 lg:block">
            {headerContent}
          </div>

          {/* Right Actions */}
          <div className="flex shrink-0 items-center gap-3" ref={notifRef}>
            {isTouchDevice && (
              <button
                type="button"
                onClick={rotateScreen}
                className="inline-flex rounded-xl p-2.5 text-slate-600 transition-all hover:bg-slate-100"
                title="Girar pantalla"
                aria-label="Cambiar orientación de pantalla"
              >
                <RotateCw size={20} />
              </button>
            )}
            {/* Notification Bell */}
            <div className="relative">
              <button
                data-testid="notification-btn"
                onClick={() => setIsNotifOpen(!isNotifOpen)}
                className={`
                  p-2.5 rounded-xl transition-all relative
                  ${isNotifOpen 
                    ? 'bg-primary/10 text-primary' 
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                  }
                `}
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>
                )}
              </button>

              {/* Notification Dropdown */}
              {isNotifOpen && (
                <div 
                  data-testid="notification-dropdown"
                  className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-scale-in z-50"
                >
                  <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-slate-50 to-white">
                    <div>
                      <h3 className="font-heading font-semibold text-slate-800">Notificaciones</h3>
                      {unreadCount > 0 && (
                        <p className="text-xs text-slate-500">{unreadCount} sin leer</p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button 
                        onClick={markAllAsRead} 
                        className="p-2 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                        title="Marcar todo como leído"
                      >
                        <Check size={16} />
                      </button>
                      <button 
                        onClick={clearNotifications} 
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                        title="Limpiar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="max-h-[400px] overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center">
                        <Bell size={40} className="mx-auto mb-3 text-slate-200" />
                        <p className="text-slate-500 text-sm">No hay notificaciones</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {notifications.map((notif) => (
                          <div
                            key={notif.id}
                            onClick={() => markAsRead(notif.id)}
                            className={`
                              p-4 hover:bg-slate-50 transition-colors cursor-pointer relative
                              ${notif.read ? 'opacity-60' : 'bg-white'}
                            `}
                          >
                            {!notif.read && (
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary"></div>
                            )}
                            <div className="pl-2">
                              <p className="font-medium text-sm text-slate-800 mb-1">{notif.title}</p>
                              <p className="text-xs text-slate-500 mb-2 leading-relaxed">{notif.message}</p>
                              <span className={`text-[10px] px-2 py-1 rounded-full border ${getBgColor(notif.type)}`}>
                                {new Date(notif.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Mobile logout */}
            <button
              data-testid="mobile-logout-btn"
              onClick={logout}
              className="sm:hidden p-2.5 rounded-xl text-red-600 hover:bg-red-50 hover:text-red-700 transition-all"
              title="Cerrar sesión"
              aria-label="Cerrar sesión"
            >
              <LogOut size={20} />
            </button>

            {/* User Avatar - Desktop */}
            <div className="hidden sm:block relative pl-3 border-l border-slate-200">
              <button
                data-testid="header-user-trigger"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-3 rounded-xl px-3 py-2 hover:bg-slate-100 transition-all"
              >
                <div className="text-right">
                  <p className="text-sm font-medium text-slate-700">{currentUser?.name}</p>
                  <p className="text-xs text-slate-400 capitalize">
                    {currentUser?.role === 'admin' ? 'Admin' : 'Colaborador'}
                  </p>
                </div>
                <div className={`
                  w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold
                  ${currentUser?.role === 'admin' 
                    ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white' 
                    : 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white'
                  }
                `}>
                  {currentUser?.avatar || currentUser?.name?.[0] || 'U'}
                </div>
              </button>

              {isUserMenuOpen && (
                <div
                  data-testid="header-user-dropdown"
                  className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-scale-in z-50"
                >
                  <div className="p-2">
                    <button
                      data-testid="header-logout-btn"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        logout();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-xl text-red-600 hover:bg-red-50 transition-all"
                    >
                      <LogOut size={16} />
                      <span>Cerrar sesión</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden p-3 sm:p-4 lg:p-2.5">
          <div className="mx-auto min-h-full w-full max-w-7xl min-w-0 lg:h-full lg:min-h-0">
            {children}
          </div>
        </main>
      </div>
    </div>
    </HeaderProvider>
  );
};

export default Layout;
